import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import {
    TokenStorageService,
    TokenType,
    MasqueradeState,
} from 'viewbid-shared-lib';
import {
    GetCurrentUser200Response,
    User,
    UsersService,
} from '@viewbid/ts-angular-client';

@Injectable({
    providedIn: 'root',
})
export class MasqueradeService {
    private readonly isMasqueradingSubject = new BehaviorSubject<boolean>(
        false,
    );
    private readonly masqueradedUserNameSubject = new BehaviorSubject<string>(
        '',
    );
    private readonly masqueradedRoleSubject = new BehaviorSubject<string>('');
    private readonly masqueradedUsernameSubject = new BehaviorSubject<string>(
        '',
    );
    /** Prevents concurrent duplicate calls to initializeMasqueradeState */
    private isInitializing = false;

    isMasquerading$ = this.isMasqueradingSubject.asObservable();
    masqueradedUserName$ = this.masqueradedUserNameSubject.asObservable();
    masqueradedRole$ = this.masqueradedRoleSubject.asObservable();
    masqueradedUsername$ = this.masqueradedUsernameSubject.asObservable();

    constructor(
        private readonly tokenStorage: TokenStorageService,
        private readonly router: Router,
        private readonly usersService: UsersService,
    ) {
        this.init();

        // Re-initialize when the admin writes masquerade state to localStorage
        // after this window has already bootstrapped (fixes the race condition
        // where the bidder app boots before tokens are available).
        window.addEventListener('storage', (event: StorageEvent) => {
            if (
                event.key === TokenType.MASQUERADE_STATE_KEY &&
                event.newValue
            ) {
                void this.initializeMasqueradeState();
            }
        });
    }

    private init(): void {
        void this.initializeMasqueradeState();
    }

    isMasquerading(): boolean {
        return this.isMasqueradingSubject.value;
    }

    getMasqueradedUserName(): string {
        return this.masqueradedUserNameSubject.value;
    }

    getMasqueradedRole(): string {
        return this.masqueradedRoleSubject.value;
    }

    getMasqueradedUsername(): string {
        return this.masqueradedUsernameSubject.value;
    }

    /**
     * Initialize masquerade state and recover session if needed
     */
    private async initializeMasqueradeState(): Promise<void> {
        // Only allow masquerade logic to execute inside the dedicated masquerade window popup
        if (window.name !== 'masquerade') {
            return;
        }

        // Prevent concurrent calls (e.g. from storage events firing multiple times)
        if (this.isInitializing) {
            return;
        }
        this.isInitializing = true;
        try {
            // Check if we're masquerading
            const masqueradeState: MasqueradeState | null =
                this.tokenStorage.getMasqueradeState();
            if (!masqueradeState?.isMasquerading) {
                return;
            }

            // Get and validate masquerade token
            const token: string | null = this.tokenStorage.getLocalStorageItem(
                TokenType.MASQUERADE_ACCESS,
            );
            if (!token) {
                this.clearMasqueradeState();
                return;
            }

            try {
                // Parse token data manually fixing base64url padding and characters
                const base64Url: string = token.split('.')[1];
                const base64: string = base64Url
                    .replace(/-/g, '+')
                    .replace(/_/g, '/');
                const pad: number = base64.length % 4;
                const paddedBase64: string = pad
                    ? base64 + '='.repeat(4 - pad)
                    : base64;
                const jsonPayload: string = decodeURIComponent(
                    atob(paddedBase64)
                        .split('')
                        .map(
                            (c: string) =>
                                '%' +
                                ('00' + c.charCodeAt(0).toString(16)).slice(-2),
                        )
                        .join(''),
                );

                const tokenData: {
                    masquerading: boolean;
                    masqueradedUserId?: string;
                    roles?: string[];
                } = JSON.parse(jsonPayload);

                // Update state with token data
                this.isMasqueradingSubject.next(true);
                this.masqueradedRoleSubject.next(tokenData.roles?.[0] ?? '');

                // Get user details
                const response: GetCurrentUser200Response =
                    await firstValueFrom(this.usersService.getCurrentUser());
                if (!response?.data?.user) {
                    throw new Error('Failed to get user details');
                }

                const user: User = response.data.user;
                this.masqueradedUsernameSubject.next(user.username ?? '');
                if (user.firstname && user.lastname) {
                    this.masqueradedUserNameSubject.next(
                        `${user.firstname} ${user.lastname}`,
                    );
                }
            } catch (error: unknown) {
                console.error('Failed to parse masquerade token', error);
                this.clearMasqueradeState();
            }
        } catch (error: unknown) {
            console.error('Failed to initialize masquerade state', error);
            this.clearMasqueradeState();
        } finally {
            this.isInitializing = false;
        }
    }

    /**
     * Clear masquerade state and redirect to login
     */
    private clearMasqueradeState(): void {
        this.tokenStorage.stopMasquerade();
        this.isMasqueradingSubject.next(false);
        this.masqueradedUserNameSubject.next('');
        this.masqueradedRoleSubject.next('');
        this.masqueradedUsernameSubject.next('');
        this.router.navigate(['/login']);
    }

    /**
     * Stop masquerading and clean up state
     */
    public stopMasquerade(): void {
        try {
            this.clearMasqueradeState();
        } catch (error: unknown) {
            console.error('Error while stopping masquerade', error);
            // Still try to clear state and redirect
            this.clearMasqueradeState();
        }
    }
}
